import { observer } from "mobx-react-lite";
import { useState } from "react";
import styled from "styled-components";

import { describeUsage } from "../../describeUsage.ts";
import { useStores } from "../../stores/RootStore.ts";
import { Button, Card, ErrorText, Muted, Page, Row, Title } from "../ui/primitives.tsx";
import { ProgressView } from "../ui/ProgressView.tsx";
import { StatusBadge } from "../ui/StatusBadge.tsx";

const Output = styled.pre`
    margin: 0;
    padding: 12px;
    border-radius: 8px;
    background: var(--background);
    font-size: 13px;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    max-height: 60vh;
    overflow-y: auto;
`;

const ErrorLine = styled.span`
    color: var(--bad);
`;

export const WorkerPage = observer(function WorkerPage({ workerKey }: { workerKey: string }) {
    const { workers, connection, ui } = useStores();
    const worker = workers.workers.get(workerKey);
    const [error, setError] = useState<string>();

    if (!worker) {
        return (
            <Page>
                <Muted>Waiting for this worker's first news…</Muted>
            </Page>
        );
    }

    async function stop() {
        const reply = await connection.command({ type: "worker.stop", controller: worker!.controller, worker: worker!.id });

        setError(reply.error);
    }

    return (
        <Page>
            <Row>
                <Button onClick={() => ui.show({ name: "workers" })}>← Workers</Button>
            </Row>
            <Card>
                <Row style={{ justifyContent: "space-between" }}>
                    <Title>{worker.title || worker.id}</Title>
                    <StatusBadge exitCode={worker.exitCode} />
                </Row>
                <Muted>
                    Worker {worker.id} on {worker.controller}, image {worker.image}
                </Muted>
                {worker.progress && <ProgressView progress={worker.progress} />}
                {worker.usage && (
                    <Muted>
                        {worker.usage.model}: {describeUsage([worker.usage], connection.models)}
                    </Muted>
                )}
                {worker.running && (
                    <Row>
                        <Button $danger onClick={stop}>
                            Stop
                        </Button>
                    </Row>
                )}
                {error && <ErrorText>{error}</ErrorText>}
            </Card>
            <Card>
                <Title as="h2" style={{ fontSize: 16 }}>
                    Output
                </Title>
                {worker.lines.length === 0 ? (
                    <Muted>Nothing yet.</Muted>
                ) : (
                    <Output>
                        {worker.lines.map((line, index) =>
                            line.stream === "stderr" ? <ErrorLine key={index}>{line.text + "\n"}</ErrorLine> : line.text + "\n",
                        )}
                    </Output>
                )}
            </Card>
        </Page>
    );
});
